import { useId } from 'react'
import { useIntl } from 'react-intl'

import { AdministrativeDivisionsAPI } from '@api/countries'

import { SortDirectionEnum } from '@models/apiBase'
import { ID } from '@models/base'
import { Option } from '@models/option'

import { PAGINATION_LIMIT } from '@/constants/constant'
import TypeUtil from '@/utils/typeUtil'

import { AsyncPaginateReactSelect, AsyncPaginateSelectProps } from '../AsyncPaginateSelect'

export type AdministrativeDivisionInputProps<IsMulti extends boolean> = Omit<
  AsyncPaginateSelectProps<IsMulti>,
  'onSearch'
> & {
  countryId?: ID
  parentId?: NonNullable<ID> | null
}

const AdministrativeDivisionInput = <IsMutli extends boolean>({
  countryId,
  parentId,
  isDisabled,
  ...props
}: AdministrativeDivisionInputProps<IsMutli>) => {
  const id = useId()
  const intl = useIntl()
  const disabled = isDisabled || !TypeUtil.isDefined(countryId) || (parentId !== undefined && parentId === null)

  const handleSearch = async (keyword: string, page: number) => {
    if (disabled) {
      return undefined
    }

    const { data } = await AdministrativeDivisionsAPI.getOptions({
      page,
      limit: PAGINATION_LIMIT,
      phrase: !TypeUtil.isEmpty(keyword) ? keyword : undefined,
      sorts: [{ field: 'name', direction: SortDirectionEnum.ASC }],
      country_id: countryId,
      parent_id: parentId,
    })

    if (data) {
      return {
        ...data,
        nodes: data.nodes.map(
          (division) =>
            new Option({
              label: division.name,
              value: division.id,
            })
        ),
      }
    }
  }

  return (
    <AsyncPaginateReactSelect
      {...props}
      key={`${id}-${countryId}-${parentId}`}
      isDisabled={disabled}
      noOptionsMessage={() => intl.formatMessage({ id: 'select.no_option' })}
      onSearch={handleSearch}
    />
  )
}

export { AdministrativeDivisionInput }
